import { Alert, Button, Grid, Skeleton, TextField } from '@mui/material';
import { Template, UpdateTemplate } from '@thro-types/notification';

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';

import {
  useBulkTemplatesQuery,
  useTemplateQuery,
  useTemplatesMutation
} from 'api/hooks/notifications';
import InfoBox from 'app/components/InfoBox';
import VariablesDescription from 'app/components/MailTemplate/VariablesDescription';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { TemplateSelect } from './components/TemplateSelect';
import { ToLeihbarButton } from './components/ToLeihbarButton';
import { initialMailTemplateValues } from './validation';

interface MailTemplateProps {
  lenderGroupId: number;
}

export const MailTemplates = ({ lenderGroupId }: MailTemplateProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: templates } = useBulkTemplatesQuery({ params: { applicationsId: 'leihbar' } });

  const [selectedTemplate, setSelectedTemplate] = useState<string>('orderSummary');

  const { data: template, isLoading } = useTemplateQuery({
    params: { name: selectedTemplate, refId: lenderGroupId, applicationsId: 'leihbar' }
  });

  const { update } = useTemplatesMutation({
    params: { generateText: true }
  });

  const { handleSubmit, register, setValue, control } = useForm({
    defaultValues: initialMailTemplateValues(template)
  });

  useEffect(() => {
    if (template) {
      setValue('subject', template.subject);
      setValue('html', template.html);
    }
  }, [setValue, template]);

  const handleTemplateUpdate = async (values: UpdateTemplate) => {
    try {
      if (!template) return;

      await update.mutateAsync({
        id: template.id,
        data: {
          subject: values.subject,
          html: values.html,
          refId: lenderGroupId.toString()
        }
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.leihbarMailSettings.alerts.templateSaveSuccessful())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.leihbarMailSettings.alerts.templateSaveFailed()))
      );
    }
  };

  const renderContent = (template: Template | undefined) => {
    if (isLoading) {
      return (
        <Grid container justifyContent={'space-between'} marginTop={10}>
          <Skeleton width={'48%'} height={'22em'} sx={{ transform: 'unset' }} />
          <Skeleton width={'48%'} height={'22em'} sx={{ transform: 'unset' }} />
        </Grid>
      );
    }
    if (!template) {
      return (
        <Grid container>
          <Alert severity="error">
            {t(translations.pages.leihbarMailSettings.alerts.loadError())}
          </Alert>
        </Grid>
      );
    }
    return (
      <form onSubmit={handleSubmit(handleTemplateUpdate)}>
        <Grid container spacing={2} justifyContent={'space-between'}>
          <Grid item xs={12} md={6}>
            <TextField
              {...register('subject')}
              className="article-messages-settings__content__subject"
              label={t(translations.common.subject())}
              variant="outlined"
              fullWidth
              size="small"
            />
            <Controller
              name="html"
              control={control}
              render={({ field }) => (
                <ReactQuill
                  className="article-messages-settings__content__rte"
                  theme="snow"
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'link', 'table'],
                      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                      ['clean']
                    ]
                  }}
                  formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Grid container justifyContent="flex-end">
              <Button
                className="article-messages-settings__content__save"
                variant="contained"
                type="submit"
              >
                {t(translations.common.save())}
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} className="article-messages-settings__content__info">
            {template.variables && (
              <InfoBox
                title={t(translations.pages.leihbarMailSettings.templateDescription.heading())}
                type="info"
              >
                <Grid container className="article-messages-settings__content__info__description">
                  {t(translations.pages.leihbarMailSettings.templateDescription.content())}
                </Grid>
                <Grid container className="article-messages-settings__content__info__placeholders">
                  <VariablesDescription
                    ignoreTextScope
                    variables={template.variables}
                    i18nDictionary={translations.pages.leihbarMailSettings.templateVariables}
                  />
                </Grid>
              </InfoBox>
            )}
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Grid container gap={2}>
      <Grid container justifyContent={'space-between'}>
        <TemplateSelect
          templates={templates}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
        />
        <ToLeihbarButton />
      </Grid>
      {renderContent(template)}
    </Grid>
  );
};
